import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../utils/api';
import NotificationInbox from '../NotificationInbox';
import { BellIcon } from './InlineIcons';
import VibanaWordmark from './VibanaWordmark';
import VibanaMark from './VibanaMark';

export default function Navbar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [workMenuOpen, setWorkMenuOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const bellRef = useRef(null);
  const isOrganizer = user?.role === 'organizer';
  const isDashboardUser = isOrganizer;
  const isA = (p) => loc.pathname === p || loc.pathname.startsWith(p + '/');

  // Fetch unread notification count when user is logged in
  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    api.get('/notifications/unread-count')
      .then(r => setUnreadCount(r.data?.count || 0))
      .catch(e => console.warn("[silent catch]", e?.message || e));
  }, [user]);

  // Close drawer on navigation
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [loc.pathname]);

  const handleBellClick = () => {
    setMenuOpen(false);
    setInboxOpen(v => !v);
    // Reset badge when opening
    if (!inboxOpen) setUnreadCount(0);
  };

  const linkStyle = (path) => ({
    fontSize:'clamp(12px, 2vw, 14px)', fontWeight: isA(path) ? 700 : 500,
    color: isA(path) ? '#FFFFFF' : 'rgba(255,255,255,0.70)',
    textDecoration:'none',
    padding:'clamp(2px, 1vw, 4px) clamp(6px, 2vw, 10px)',
    borderRadius:6,
    background: isA(path) ? 'rgba(230,57,70,0.12)' : 'transparent',
    transition:'all 240ms cubic-bezier(0.16,1,0.3,1)',
    whiteSpace:'nowrap',
    position: 'relative',
  });

  const drawerLinkStyle = {
    display: 'block',
    padding: '14px 20px',
    color: 'rgba(255,255,255,0.95)',
    textDecoration: 'none',
    fontSize: 16,
    fontWeight: 600,
    borderBottom: '1px solid rgba(255,255,255,0.07)',
  };

  const closeDrawer = () => setMobileDrawerOpen(false);

  return (
    <>
      <nav style={{
        background:'rgba(12,11,10,0.72)',
        backdropFilter:'saturate(1.6) blur(20px)',
        WebkitBackdropFilter:'saturate(1.6) blur(20px)',
        height:76, display:'flex', alignItems:'center',
        padding:'0 24px', position:'sticky', top:0, zIndex:100,
        borderBottom:'1px solid rgba(217,168,59,0.08)',
      }}>
        <style>{`
          .vb-nav-link{transition:all 240ms cubic-bezier(0.16,1,0.3,1)}
          .vb-nav-link:hover{color:#E63946 !important; transform: translateY(-1px)}
          .vb-nav-link:focus-visible{outline:2px solid #E9C061;outline-offset:2px}
          .vb-nav-get-app{transition:all 240ms cubic-bezier(0.16,1,0.3,1)}
          .vb-nav-get-app:hover{background:rgba(255,255,255,0.16) !important; transform: translateY(-1px)}
          .vb-nav-get-app:focus-visible{outline:2px solid #E9C061;outline-offset:2px}
          @media (max-width: 900px) {
            .vb-nav-center { display: none !important; }
            .vb-nav-get-app { display: none !important; }
            .vb-nav-org-cta { display: none !important; }
            .vb-nav-auth-link {
              padding: 6px 10px !important;
              font-size: 12px !important;
            }
            .vb-hamburger { display: flex !important; }
          }
          .vb-hamburger { display: none; }
        `}</style>

        {/* Logo with SVG wordmark */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize:23, fontWeight:500, color:'rgba(251,244,230,0.98)', textDecoration:'none',
          fontFamily:'var(--font-display)', fontOpticalSizing:'auto', letterSpacing:'-0.02em', marginRight:24, flexShrink:0 }}>
          <VibanaWordmark />
          vibana
        </Link>

        {/* Center links  -  role based */}
       <div className="vb-nav-center" style={{ display:'flex', alignItems:'center', gap:4, flex:1 }}>
    {user ? (
      <>
        <Link to="/" className="vb-nav-link" style={linkStyle('/')}>Home</Link>
        <Link to="/events" className="vb-nav-link" style={linkStyle('/events')}>Events</Link>
        <Link to="/magazine" className="vb-nav-link" style={linkStyle('/magazine')}>Magazine</Link>
        {user.role === 'support' || user.role === 'admin' ? (
          <Link to="/support" className="vb-nav-link" style={linkStyle('/support')}>
            Support Dashboard
          </Link>
        ) : user.role === 'organizer' ? (
          <>
            <Link to="/dashboard" className="vb-nav-link" style={linkStyle('/dashboard')}>Dashboard</Link>
            <Link to="/organizer/earnings" className="vb-nav-link" style={linkStyle('/organizer/earnings')}>Earnings</Link>
            <Link to="/promote-event" className="vb-nav-link" style={linkStyle('/promote-event')}>Promote Event</Link>
          </>
        ) : isDashboardUser ? (
          <Link to="/organizer" className="vb-nav-link" style={linkStyle('/organizer')}>Dashboard</Link>
        ) : null}
      </>
    ) : (
      <>
        <Link to="/events" className="vb-nav-link" style={linkStyle('/events')}>Events</Link>
        <Link to="/magazine" className="vb-nav-link" style={linkStyle('/magazine')}>Magazine</Link>
        <div
          onMouseEnter={() => setWorkMenuOpen(true)}
          onMouseLeave={() => setWorkMenuOpen(false)}
          style={{ position: 'relative' }}
        >
          <button
            onClick={() => setWorkMenuOpen(v => !v)}
            className="vb-nav-link"
            style={{ ...linkStyle('/work-with-us'), border: 'none', background: 'transparent', cursor: 'pointer' }}
          >
            Work with us ▾
          </button>
          {workMenuOpen && (
            <div style={{ position: 'absolute', top: '100%', left: 0, paddingTop: 6, zIndex: 120 }}>
              <div style={{ background: '#151210', borderRadius: 10, border: '1px solid rgba(217,168,59,0.16)', minWidth: 220, boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
                <Link to="/work-with-us/organizers" onClick={() => setWorkMenuOpen(false)} style={{ display: 'block', padding: '10px 12px', textDecoration: 'none', color: 'rgba(255,255,255,0.90)', fontSize: 13 }}>Organizers / Promoters</Link>
                <Link to="/partners" onClick={() => setWorkMenuOpen(false)} style={{ display: 'block', padding: '10px 12px', textDecoration: 'none', color: 'rgba(255,255,255,0.90)', fontSize: 13 }}>Partners &amp; Brands</Link>
              </div>
            </div>
          )}
        </div>
      </>
    )}
  </div>

        {/* Right */}
        <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:'auto' }}>
          <Link to="/download" style={{ fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.95)',
            textDecoration:'none', background:'rgba(255,255,255,0.10)', padding:'9px 16px', borderRadius:999, letterSpacing:0.4,
            border:'1px solid rgba(255,255,255,0.12)', transition:'background 0.15s' }}
            className="vb-nav-get-app">
            GET THE APP
          </Link>
          {user ? (
            <>
              {isOrganizer && (
                <Link to="/submit-event" style={{ fontSize:13, fontWeight:700, color:'#fff',
                  textDecoration:'none', background:'var(--red)', padding:'8px 16px', borderRadius:7 }} className="vb-nav-org-cta">
                  + Post Event
                </Link>
              )}
              {/* Bell icon with unread badge */}
              <div ref={bellRef} style={{ position:'relative' }}>
                <button
                  onClick={handleBellClick}
                  aria-label="Notifications"
                  style={{
                    width:34, height:34, borderRadius:17,
                    background:'rgba(255,255,255,0.08)',
                    border:'1px solid rgba(255,255,255,0.10)',
                    cursor:'pointer', color:'rgba(250,247,242,0.75)', fontSize:16,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    position:'relative',
                  }}
                  aria-label="Notifications"
                >
                  <BellIcon size={16} strokeWidth={2} />
                  {unreadCount > 0 && (
                    <span style={{
                      position:'absolute', top:2, right:2,
                      width:9, height:9, borderRadius:'50%',
                      background:'var(--red)', border:'1.5px solid rgba(8,8,8,0.95)',
                    }} />
                  )}
                </button>
                <NotificationInbox open={inboxOpen} onClose={() => setInboxOpen(false)} />
              </div>
              <div style={{ position:'relative' }}>
                <button onClick={() => setMenuOpen(!menuOpen)} aria-label="Account menu" style={{
                  width:34, height:34, borderRadius:17, background:'var(--red)',
                  border:'none', cursor:'pointer', color:'#fff', fontSize:13, fontWeight:800 }}>
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </button>
                {menuOpen && (
                  <>
                    <div style={{ position:'fixed', inset:0, zIndex:99 }} onClick={() => setMenuOpen(false)} />
                    <div style={{ position:'absolute', top:'calc(100% + 8px)', right:0, background:'#151210',
                      borderRadius:12, boxShadow:'0 12px 40px rgba(0,0,0,0.6)', border:'1px solid rgba(217,168,59,0.16)',
                      padding:'6px 0', minWidth:180, zIndex:100 }}>
                      <div style={{ padding:'10px 16px 6px', fontSize:12, color:'rgba(255,255,255,0.35)' }}>{user.email}</div>
                      <div style={{ height:1, background:'rgba(255,255,255,0.07)', margin:'6px 0' }} />
                      <Link to="/profile" onClick={() => setMenuOpen(false)}
                        style={{ display:'block', padding:'10px 16px', fontSize:14, color:'rgba(250,247,242,0.90)', textDecoration:'none' }}>
                        Profile
                      </Link>
                      <div style={{ height:1, background:'rgba(255,255,255,0.07)', margin:'6px 0' }} />
                      <Link to="/help" onClick={() => setMenuOpen(false)}
                        style={{ display:'block', padding:'10px 16px', fontSize:14, color:'rgba(250,247,242,0.90)', textDecoration:'none' }}>
                        Help
                      </Link>
                      <div style={{ height:1, background:'rgba(255,255,255,0.07)', margin:'6px 0' }} />
                      <button onClick={() => { logout(); setMenuOpen(false); nav('/'); }}
                        style={{ display:'block', width:'100%', textAlign:'left', padding:'10px 16px',
                          fontSize:14, color:'var(--red)', background:'none', border:'none', cursor:'pointer' }}>
                        Log out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" style={{ fontSize:13, fontWeight:600, color:'rgba(250,247,242,0.85)',
                textDecoration:'none', padding:'7px 16px', border:'1px solid rgba(255,255,255,0.12)', borderRadius:7 }}
                className="vb-nav-auth-link">
                Log in
              </Link>
              <Link to="/register" style={{ fontSize:13, fontWeight:700, color:'#fff',
                textDecoration:'none', background:'var(--red)', padding:'8px 18px', borderRadius:7 }}
                className="vb-nav-auth-link">
                Get started
              </Link>
            </>
          )}

          {/* Hamburger button — visible only at ≤900px */}
          <button
            className="vb-hamburger"
            onClick={() => setMobileDrawerOpen(true)}
            aria-label="Open menu"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px 4px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 5,
              marginLeft: 4,
            }}
          >
            <span style={{ display: 'block', width: 22, height: 2, background: 'rgba(250,247,242,0.90)', borderRadius: 2 }} />
            <span style={{ display: 'block', width: 22, height: 2, background: 'rgba(250,247,242,0.90)', borderRadius: 2 }} />
            <span style={{ display: 'block', width: 22, height: 2, background: 'rgba(250,247,242,0.90)', borderRadius: 2 }} />
          </button>
        </div>
      </nav>

      {/* Mobile drawer backdrop */}
      {mobileDrawerOpen && (
        <div
          onClick={closeDrawer}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 200,
          }}
        />
      )}

      {/* Mobile slide-out drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: 280,
          height: '100%',
          background: '#100D0A',
          zIndex: 201,
          transform: mobileDrawerOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.28s cubic-bezier(0.16,1,0.3,1)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Drawer header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <span style={{ fontSize: 21, fontWeight: 500, color: '#fff', fontFamily: 'var(--font-display)', fontOpticalSizing: 'auto', letterSpacing: '-0.02em', display: 'inline-flex', alignItems: 'center' }}>
            <VibanaMark size={20} title="" style={{ marginRight: 6 }} />vibana
          </span>
          <button
            onClick={closeDrawer}
            aria-label="Close menu"
            style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.8)', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            ✕
          </button>
        </div>

        {/* Drawer nav links */}
        <nav style={{ flex: 1 }}>
          <Link to="/events" onClick={closeDrawer} style={drawerLinkStyle}>Events</Link>
          <Link to="/magazine" onClick={closeDrawer} style={drawerLinkStyle}>Magazine</Link>
          <Link to="/work-with-us/organizers" onClick={closeDrawer} style={drawerLinkStyle}>Work With Us</Link>

          {user ? (
            <>
              <Link to="/" onClick={closeDrawer} style={drawerLinkStyle}>Home</Link>
              {user.role === 'organizer' && (
                <>
                  <Link to="/dashboard" onClick={closeDrawer} style={drawerLinkStyle}>Dashboard</Link>
                  <Link to="/submit-event" onClick={closeDrawer} style={drawerLinkStyle}>Post Event</Link>
                </>
              )}
              <Link to="/profile" onClick={closeDrawer} style={drawerLinkStyle}>Profile</Link>
              <Link to="/help" onClick={closeDrawer} style={drawerLinkStyle}>Help</Link>
              <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 'auto' }}>
                <button
                  onClick={() => { logout(); closeDrawer(); nav('/'); }}
                  style={{ background: 'none', border: 'none', color: '#E63946', fontSize: 16, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                >
                  Log out
                </button>
              </div>
            </>
          ) : (
            <div style={{ padding: '20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Link
                to="/login"
                onClick={closeDrawer}
                style={{ display: 'block', textAlign: 'center', padding: '12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(250,247,242,0.95)', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}
              >
                Log In
              </Link>
              <Link
                to="/register"
                onClick={closeDrawer}
                style={{ display: 'block', textAlign: 'center', padding: '12px', borderRadius: 8, background: '#E63946', color: '#fff', textDecoration: 'none', fontWeight: 800, fontSize: 14 }}
              >
                Get Started
              </Link>
            </div>
          )}
        </nav>
      </div>
    </>
  );
}
